import {
  createContext,
  use,
  useEffect,
  useState,
  type PropsWithChildren,
} from 'react';

export type Theme = 'dark' | 'light' | 'system';

interface Props {
  defaultTheme?: Theme;
  storageKey?: string;
}

interface State {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const initialState: State = {
  theme: 'system',
  setTheme: () => null,
};

const ThemeProviderContext = createContext<State>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'vizitpro-theme',
  ...props
}: PropsWithChildren<Props>) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme,
  );

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove('light', 'dark');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
        .matches
        ? 'dark'
        : 'light';

      root.classList.add(systemTheme);
      return;
    }

    root.classList.add(theme);
  }, [theme]);

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
  };

  return (
    <ThemeProviderContext {...props} value={value}>
      {children}
    </ThemeProviderContext>
  );
}

export function useTheme() {
  const context = use(ThemeProviderContext);

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider');

  return context;
}
